import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CalendarIcon, RefreshCw, CloudDownload, BarChart3 } from "lucide-react";
import { format, subDays } from "date-fns";
import CampaignMetricsGrid from './CampaignMetricsGrid';
import CampaignDetailsModal from './CampaignDetailsModal';
import FunnelOverviewCards from './FunnelOverviewCards';
import FunnelStageCard from './FunnelStageCard';
import CreativeQualityGrid from './CreativeQualityGrid';
import AttributionJourney from './AttributionJourney';
import AutomatedInsights from './AutomatedInsights';
import { useCampaignMetrics } from '@/hooks/useCampaignMetrics';
import { useFunnelPerformance } from '@/hooks/useFunnelPerformance';
import { useCreativeQuality } from '@/hooks/useCreativeQuality';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
export default function FacebookAdsDashboard() {
  const { toast } = useToast();
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('T-Visa');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  const { data: campaignData, isLoading: campaignLoading, refetch: refetchCampaigns } = useCampaignMetrics(dateRange);
  const { data: funnelData, isLoading: funnelLoading, refetch: refetchFunnel } = useFunnelPerformance(selectedProduct, dateRange);
  const { data: creativeData, isLoading: creativeLoading } = useCreativeQuality(selectedProduct);

  const refetch = async () => {
    await Promise.all([refetchCampaigns(), refetchFunnel()]);
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      
      // Garante que temos datas válidas
      const from = dateRange.from || subDays(new Date(), 30);
      const to = dateRange.to || new Date();
      
      const body = {
        fullSync: true,
        dateRange: {
          start: format(from, 'yyyy-MM-dd'),
          end: format(to, 'yyyy-MM-dd'),
        },
      };
      const { data, error } = await supabase.functions.invoke('facebook-ads-sync', { body });
      if (error) throw error;
      toast({
        title: 'Sync completed',
        description: `${data?.totalCampaigns || 0} campaigns • ${data?.totalMetricsSynced || 0} records`,
      });
      await refetch();
    } catch (e) {
      const message = e?.message || 'Tente novamente mais tarde';
      toast({ title: 'Erro ao sincronizar', description: message, variant: 'destructive' });
      console.error('Sync error:', e);
    } finally {
      setIsSyncing(false);
    }
  };

  // Available products (in real app, fetch from campaigns)
  const products = ['T-Visa', 'EB2-NIW', 'SIJS', 'U-Visa', 'VAWA'];

  // Filtra campanhas que tiveram gasto no período selecionado
  const filteredCampaigns = showOnlyActive 
    ? campaignData?.filter(c => c.metrics?.spend > 0)
    : campaignData;

  // Calcula totais gerais
  const totalMetrics = filteredCampaigns?.reduce((acc, campaign) => ({
    spend: acc.spend + (campaign.metrics?.spend || 0),
    leads: acc.leads + (campaign.metrics?.leads || 0),
    impressions: acc.impressions + (campaign.metrics?.impressions || 0),
    clicks: acc.clicks + (campaign.metrics?.clicks || 0),
  }), { spend: 0, leads: 0, impressions: 0, clicks: 0 }) || { spend: 0, leads: 0, impressions: 0, clicks: 0 };

  const avgCPL = totalMetrics.leads > 0 ? totalMetrics.spend / totalMetrics.leads : 0;
  const avgCTR = totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Facebook Ads Dashboard</h1>
          <p className="text-muted-foreground mt-1">Métricas principais das suas campanhas</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Product Selector */}
          <Select value={selectedProduct} onValueChange={setSelectedProduct}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {products.map(product => (
                <SelectItem key={product} value={product}>{product}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Date Range Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <CalendarIcon className="h-4 w-4" />
                {dateRange.from && dateRange.to ? (
                  `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`
                ) : (
                  'Selecione período'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-3 border-b flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDateRange({ from: subDays(new Date(), 30), to: new Date() })}
                >
                  Last 30 days
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDateRange({ from: subDays(new Date(), 60), to: new Date() })}
                >
                  Last 60 days
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDateRange({ from: subDays(new Date(), 90), to: new Date() })}
                >
                  Last 90 days
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                    setDateRange({ from: firstDay, to: now });
                  }}
                >
                  This Month
                </Button>
              </div>
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={(range) => {
                  if (range?.from && range?.to) {
                    setDateRange({ from: range.from, to: range.to });
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          {/* Sync from Facebook */}
          <Button onClick={handleSync} variant="default" className="gap-2" disabled={isSyncing}>
            <CloudDownload className="h-4 w-4" />
            {isSyncing ? 'Sincronizando...' : 'Sync Facebook'}
          </Button>

          {/* Refresh local data */}
          <Button onClick={() => refetch()} size="icon" variant="outline" disabled={funnelLoading || creativeLoading}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Gasto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalMetrics.spend.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground mt-1">{filteredCampaigns?.length || 0} campanhas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalMetrics.leads.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">Período selecionado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">CPL Médio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${avgCPL.toFixed(2)}</div>
            <p className={`text-xs mt-1 ${
              avgCPL < 15 ? 'text-green-600' : avgCPL < 30 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {avgCPL < 15 ? 'Excelente' : avgCPL < 30 ? 'Bom' : 'Alto'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">CTR Médio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgCTR.toFixed(2)}%</div>
            <p className="text-xs text-muted-foreground mt-1">{totalMetrics.clicks.toLocaleString()} clicks</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Navigation */}
      <Tabs defaultValue="campaigns" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="campaigns" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Campanhas
          </TabsTrigger>
          <TabsTrigger value="funnel" className="gap-2">
            📊 Funil Completo
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-foreground">Métricas por Campanha</h2>
            <div className="flex items-center gap-2">
              <Switch
                id="active-only"
                checked={showOnlyActive}
                onCheckedChange={setShowOnlyActive}
              />
              <Label htmlFor="active-only" className="text-sm cursor-pointer">
                Apenas com gasto no período
              </Label>
            </div>
          </div>
          <CampaignMetricsGrid 
            campaigns={filteredCampaigns} 
            isLoading={campaignLoading}
            onCampaignClick={(campaign) => setSelectedCampaign(campaign)}
          />
        </TabsContent>

        {/* Funnel Tab */}
        <TabsContent value="funnel" className="space-y-6">
          {/* Product Selector for Funnel */}
          <div className="flex items-center gap-3">
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                {products.map(product => (
                  <SelectItem key={product} value={product}>{product}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Funnel Overview Cards */}
          {funnelData && funnelData.overview.totalLeads > 0 && <FunnelOverviewCards overview={funnelData.overview} />}

          {/* Performance por Estágio */}
          {funnelData && funnelData.stages.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">Performance por Estágio</h2>
              {funnelData.stages.map(stage => (
                <FunnelStageCard key={stage.funnel_stage} stage={stage} />
              ))}
            
            {/* Full Funnel ROAS Callout */}
            {funnelData && (
              <Card className="border-primary bg-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">💡 ROAS Real do Funil Completo</h3>
                      <p className="text-muted-foreground mt-1">
                        Revenue ${funnelData.overview.revenue.toLocaleString()} / Total Spend ${funnelData.overview.totalFunnelSpend.toLocaleString()} = {funnelData.overview.funnelRoas}x ROAS
                      </p>
                    </div>
                    <div className="text-4xl font-bold text-primary">{funnelData.overview.funnelRoas}x</div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    (Esse é o número que importa - considera todo o gasto do funil!)
                  </p>
                </CardContent>
              </Card>
            )}
            </div>
          )}

          {/* Creative Quality Score */}
          {creativeData && creativeData.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">🎨 Creative Quality Score</h2>
              <p className="text-muted-foreground">Top performers ordenados por engagement rate</p>
              <CreativeQualityGrid creatives={creativeData} />
            </div>
          )}

          {/* Attribution Journey Sample */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">📈 Attribution Journey</h2>
            <AttributionJourney />
          </div>

          {/* Automated Insights */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-foreground">🎯 Actionable Insights</h2>
            <AutomatedInsights productName={selectedProduct} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Campaign Details Modal */}
      <CampaignDetailsModal
        campaign={selectedCampaign}
        dateRange={dateRange}
        open={!!selectedCampaign}
        onOpenChange={(open) => !open && setSelectedCampaign(null)}
      />
    </div>
  );
}
